import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { networkManager, getErrorMessage } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Loader2,
  Check,
  FileDown,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Radio,
  Play,
  GitBranch,
  ExternalLink,
} from 'lucide-react';

/** Matches backend SummarizeResult from ai.service.ts */
interface SummarizeResult {
  summary: string;
  strengths: string[];
  risks: string[];
  questions: string[];
}

/* ---------- Types ---------- */

interface Submission {
  submissionId: string;
  title: string;
  problem: string;
  solution: string;
  impact: string;
  technicalApproach: string;
  status: string;
  demoUrl?: string;
  repoUrl?: string;
  fileUrls: string[];
}

interface Criterion {
  name: string;
  weight: number;
  maxScore: number;
}

interface EventData {
  eventId: string;
  name: string;
  scoringCriteria: Criterion[];
}

interface TeamItem {
  teamId: string;
  name: string;
  teamPhotoUrl?: string;
}

interface ApiResponse<T> {
  status: boolean;
  data: T;
}

const SUBMISSION_SECTIONS = [
  { key: 'problem' as const, label: 'Problem Statement' },
  { key: 'solution' as const, label: 'Solution' },
  { key: 'impact' as const, label: 'Impact' },
  { key: 'technicalApproach' as const, label: 'Technical Approach' },
];

/* ---------- Component ---------- */

export function JudgingPanelPage() {
  const { eventId, teamId } = useParams<{ eventId: string; teamId: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canSummarize = can('ai:summarize');
  const canViewAllScores = can('score:view-all');

  const [submission, setSubmission] = useState<Submission | null>(null);

  // AI Summarize state
  const [summarizing, setSummarizing] = useState(false);
  const [summaryResult, setSummaryResult] = useState<SummarizeResult | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // AI Judging Summary state
  const [judgingSummaryLoading, setJudgingSummaryLoading] = useState(false);
  const [judgingSummary, setJudgingSummary] = useState<{ eventSummary: string; teamSummaries: Array<{ teamId: string; teamName: string; averageScore: number; strengths: string; weaknesses: string; keyTakeaways: string }> } | null>(null);
  const [judgingSummaryError, setJudgingSummaryError] = useState<string | null>(null);
  const [judgingSummaryExpanded, setJudgingSummaryExpanded] = useState(true);
  const [event, setEvent] = useState<EventData | null>(null);
  const [teams, setTeams] = useState<TeamItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scores: keyed by criterion name
  const [scores, setScores] = useState<Record<string, number>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [submittingScores, setSubmittingScores] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Live session state
  const [liveSession, setLiveSession] = useState<{ currentTeamId: string | null; timerState: string; timerRemainingSeconds: number; timerDurationSeconds: number; teamOrder: Array<{ teamId: string; teamName: string; status: string }> } | null>(null);
  const [liveRemaining, setLiveRemaining] = useState(0);
  const liveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const livePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll live session
  const fetchLiveSession = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await networkManager.get<{ status: boolean; data: { currentTeamId: string | null; timerState: string; timerRemainingSeconds: number; timerDurationSeconds: number; status: string; teamOrder: Array<{ teamId: string; teamName: string; status: string }> } }>(
        `/api/events/${eventId}/live`,
      );
      if (res.data.status !== 'ended') {
        setLiveSession(res.data);
        setLiveRemaining(res.data.timerRemainingSeconds);
      } else {
        setLiveSession(null);
      }
    } catch {
      setLiveSession(null);
    }
  }, [eventId]);

  useEffect(() => {
    fetchLiveSession();
    livePollRef.current = setInterval(fetchLiveSession, 3000);
    return () => { if (livePollRef.current) clearInterval(livePollRef.current); };
  }, [fetchLiveSession]);

  // Local countdown
  useEffect(() => {
    if (liveTimerRef.current) clearInterval(liveTimerRef.current);
    if (!liveSession || liveSession.timerState !== 'running') return;
    liveTimerRef.current = setInterval(() => setLiveRemaining((p) => Math.max(0, p - 1)), 1000);
    return () => { if (liveTimerRef.current) clearInterval(liveTimerRef.current); };
  }, [liveSession?.timerState, liveSession?.timerRemainingSeconds]);

  // Load submission + event + existing scores in parallel
  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      setSubmitted(false);
      setComments({});
      setScores({});
      setSummaryResult(null);
      setSummaryError(null);
      try {
        const [subRes, eventRes, teamsRes, scoresRes] = await Promise.all([
          networkManager.get<ApiResponse<Submission>>(
            `/api/events/${eventId}/teams/${teamId}/submissions`,
          ),
          networkManager.get<ApiResponse<EventData>>(`/api/events/${eventId}`),
          networkManager.get<{ data: TeamItem[] }>(`/api/events/${eventId}/teams`).catch(() => ({ data: [] as TeamItem[] })),
          networkManager.get<ApiResponse<Array<{ criteria: Array<{ name: string; score: number; comment?: string }> }>>>(
            `/api/events/${eventId}/scores/teams/${teamId}`,
          ).catch(() => ({ data: [] })),
        ]);
        setSubmission(subRes.data);
        setEvent(eventRes.data);
        setTeams(teamsRes.data);

        // Check if judge already has scores for this team
        const existingScores = Array.isArray(scoresRes.data) && scoresRes.data.length > 0
          ? scoresRes.data[0]
          : null;

        if (existingScores) {
          // Load existing scores and comments
          const loaded: Record<string, number> = {};
          const loadedComments: Record<string, string> = {};
          for (const c of eventRes.data.scoringCriteria) {
            const existing = existingScores.criteria.find((sc) => sc.name === c.name);
            loaded[c.name] = existing?.score ?? 0;
            loadedComments[c.name] = existing?.comment ?? '';
          }
          setScores(loaded);
          setComments(loadedComments);
          setSubmitted(true);
        } else {
          // Initialize scores to 0
          const initial: Record<string, number> = {};
          const initialComments: Record<string, string> = {};
          for (const c of eventRes.data.scoringCriteria) {
            initial[c.name] = 0;
            initialComments[c.name] = '';
          }
          setScores(initial);
          setComments(initialComments);
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to load data';
        setError(msg);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [eventId, teamId]);

  // Weighted total
  const weightedTotal = useMemo(() => {
    if (!event) return 0;
    return event.scoringCriteria.reduce((sum, c) => {
      const score = scores[c.name] ?? 0;
      const normalized = c.maxScore > 0 ? score / c.maxScore : 0;
      return sum + normalized * c.weight;
    }, 0);
  }, [event, scores]);

  const maxWeightedTotal = useMemo(() => {
    if (!event) return 100;
    return event.scoringCriteria.reduce((sum, c) => sum + c.weight, 0);
  }, [event]);

  // Team navigation
  const currentIdx = teams.findIndex((t) => t.teamId === teamId);
  const prevTeam = currentIdx > 0 ? teams[currentIdx - 1] : null;
  const nextTeam = currentIdx >= 0 && currentIdx < teams.length - 1 ? teams[currentIdx + 1] : null;

  function handleScoreChange(criterionName: string, value: number, maxScore: number) {
    const clamped = Math.max(0, Math.min(maxScore, value));
    setScores((prev) => ({ ...prev, [criterionName]: clamped }));
  }

  async function handleSubmitScores() {
    if (!event) return;
    setSubmittingScores(true);
    setError(null);
    try {
      await networkManager.post<ApiResponse<unknown>>(`/api/events/${eventId}/scores`, {
        teamId,
        criteria: event.scoringCriteria.map((c) => ({
          name: c.name,
          score: scores[c.name] ?? 0,
          comment: comments[c.name]?.trim() || undefined,
        })),
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to submit scores';
      setError(msg);
    } finally {
      setSubmittingScores(false);
    }
  }

  async function handleGenerateJudgingSummary() {
    if (!eventId) return;
    setJudgingSummaryLoading(true);
    setJudgingSummaryError(null);
    try {
      const res = await networkManager.post<{ status: boolean; data: { eventSummary: string; teamSummaries: Array<{ teamId: string; teamName: string; averageScore: number; strengths: string; weaknesses: string; keyTakeaways: string }> } }>(
        `/api/events/${eventId}/scores/judging-summary`,
        {},
      );
      setJudgingSummary(res.data);
      setJudgingSummaryExpanded(true);
    } catch (err) {
      setJudgingSummaryError(getErrorMessage(err, 'Failed to generate judging summary'));
    } finally {
      setJudgingSummaryLoading(false);
    }
  }

  async function handleSummarize() {
    if (!eventId || !teamId) return;
    setSummarizing(true);
    setSummaryError(null);
    try {
      const res = await networkManager.post<{ status: boolean; data: SummarizeResult }>(
        '/api/ai/summarize',
        { eventId, teamId },
      );
      setSummaryResult(res.data);
      setSummaryExpanded(true);
    } catch (err) {
      setSummaryError(getErrorMessage(err, 'AI summarization failed — please try again later'));
    } finally {
      setSummarizing(false);
    }
  }

  /* ---------- Render states ---------- */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if ((error && !submission) || !submission || !event) {
    return (
      <div className="animate-fade-in">
        <button onClick={() => navigate(-1)} className="btn-ghost mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error || 'Data not found'}
        </div>
      </div>
    );
  }

  /* ---------- Main layout ---------- */

  return (
    <div className="animate-fade-in">
      {/* Top bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button onClick={() => navigate(-1)} className="btn-ghost">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {/* Team nav */}
        <div className="flex items-center gap-2">
          {prevTeam ? (
            <Link
              to={`/events/${eventId}/judge/${prevTeam.teamId}`}
              className="btn-secondary flex items-center gap-1 text-sm"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </Link>
          ) : (
            <button className="btn-secondary flex items-center gap-1 text-sm" disabled>
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
          )}
          {currentIdx >= 0 && (
            <span className="text-sm text-ink-secondary">
              {currentIdx + 1} / {teams.length}
            </span>
          )}
          {nextTeam ? (
            <Link
              to={`/events/${eventId}/judge/${nextTeam.teamId}`}
              className="btn-secondary flex items-center gap-1 text-sm"
            >
              Next <ChevronRight className="h-4 w-4" />
            </Link>
          ) : (
            <button className="btn-secondary flex items-center gap-1 text-sm" disabled>
              Next <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Live session bar */}
      {liveSession && (
        <div className="mt-3 card p-3 flex items-center justify-between bg-purple-50 border-purple-200">
          <div className="flex items-center gap-3">
            <Radio className="h-4 w-4 text-purple-600 animate-pulse" />
            <div>
              <p className="text-xs font-medium text-purple-700">
                {liveSession.currentTeamId === teamId ? 'This team is presenting now' : 'Live session active'}
              </p>
              {liveSession.currentTeamId && liveSession.currentTeamId !== teamId && (
                <p className="text-xs text-purple-600">
                  Presenting: {liveSession.teamOrder.find((t) => t.status === 'presenting')?.teamName}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`font-mono text-2xl font-bold ${
              liveSession.timerState === 'expired' || liveRemaining <= 0 ? 'text-red-500' :
              liveRemaining <= 30 ? 'text-yellow-500' : 'text-green-500'
            }`}>
              {String(Math.floor(Math.max(0, liveRemaining) / 60)).padStart(2, '0')}:{String(Math.max(0, liveRemaining) % 60).padStart(2, '0')}
            </span>
            {liveSession.currentTeamId && liveSession.currentTeamId !== teamId && (
              <Link
                to={`/events/${eventId}/judge/${liveSession.currentTeamId}`}
                className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"
              >
                <Radio className="h-3 w-3" /> Sync
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        {(() => {
          const currentTeam = teams.find((t) => t.teamId === teamId);
          if (currentTeam?.teamPhotoUrl) {
            return (
              <img
                src={currentTeam.teamPhotoUrl}
                alt={`${currentTeam.name} team photo`}
                className="h-12 w-12 rounded-lg object-cover border border-gray-200"
              />
            );
          }
          if (currentTeam) {
            return (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 border border-gray-200 text-lg font-bold text-ink-tertiary">
                {currentTeam.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
            );
          }
          return null;
        })()}
        <div>
          <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">
            {teams.find((t) => t.teamId === teamId)?.name ?? 'Team'}
          </h1>
          <p className="text-sm text-ink-secondary">{submission.title || 'Untitled submission'}</p>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Split layout: stack on mobile, side by side on lg+ */}
      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        {/* Left: Submission read-only */}
        <div className="flex-1 space-y-4 lg:min-w-0">
          {/* AI Summary for judges */}
          {canSummarize && (
            <div>
              {!summaryResult && (
                <button
                  className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                  onClick={handleSummarize}
                  disabled={summarizing}
                >
                  {summarizing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {summarizing ? 'Generating AI Summary...' : 'AI Summary'}
                </button>
              )}
              {summaryError && (
                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {summaryError}
                </div>
              )}
              {summaryResult && (
                <div className="card border-purple-200 bg-purple-50/30 p-4">
                  <button
                    className="flex w-full items-center justify-between"
                    onClick={() => setSummaryExpanded(!summaryExpanded)}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                      <Sparkles className="h-4 w-4" /> AI Summary
                    </span>
                    {summaryExpanded ? (
                      <ChevronUp className="h-4 w-4 text-purple-600" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-purple-600" />
                    )}
                  </button>
                  {summaryExpanded && (
                    <div className="mt-3 space-y-3">
                      <p className="text-sm text-ink leading-relaxed">{summaryResult.summary}</p>
                      {summaryResult.strengths.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-green-700">Strengths</h4>
                          <ul className="mt-1 list-disc pl-4 text-sm text-green-800 space-y-0.5">
                            {summaryResult.strengths.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                      {summaryResult.risks.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-amber-700">Risks</h4>
                          <ul className="mt-1 list-disc pl-4 text-sm text-amber-800 space-y-0.5">
                            {summaryResult.risks.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        </div>
                      )}
                      {summaryResult.questions.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-blue-700">Questions to Ask</h4>
                          <ul className="mt-1 list-disc pl-4 text-sm text-blue-800 space-y-0.5">
                            {summaryResult.questions.map((q, i) => <li key={i}>{q}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {SUBMISSION_SECTIONS.map((section) => {
            const content = submission[section.key];
            return (
              <section key={section.key} className="card p-5">
                <h2 className="font-semibold text-ink">{section.label}</h2>
                <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink-secondary">
                  {content?.trim() ? (
                    content
                  ) : (
                    <span className="italic text-ink-tertiary">No content provided</span>
                  )}
                </div>
              </section>
            );
          })}

          {/* Links & Resources */}
          <section className="card p-5">
            <h2 className="font-semibold text-ink">Links & Resources</h2>
            <div className="mt-3 space-y-2">
              {submission.demoUrl ? (
                <a
                  href={submission.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-ink hover:underline"
                >
                  <Play className="h-4 w-4 text-ink-tertiary" />
                  <span className="flex-1 truncate">{submission.demoUrl}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
                </a>
              ) : (
                <div className="flex items-center gap-2 text-sm text-ink-tertiary italic">
                  <Play className="h-4 w-4" />
                  No demo / video link provided
                </div>
              )}
              {submission.repoUrl ? (
                <a
                  href={submission.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-ink hover:underline"
                >
                  <GitBranch className="h-4 w-4 text-ink-tertiary" />
                  <span className="flex-1 truncate">{submission.repoUrl}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
                </a>
              ) : (
                <div className="flex items-center gap-2 text-sm text-ink-tertiary italic">
                  <GitBranch className="h-4 w-4" />
                  No repository link provided
                </div>
              )}
            </div>
          </section>

          {/* Attachments */}
          {submission.fileUrls && submission.fileUrls.length > 0 && (
            <section className="card p-5">
              <h2 className="font-semibold text-ink">Attachments</h2>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {submission.fileUrls.map((url, i) => {
                  const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(url);
                  return isImage ? (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={url} alt={`Attachment ${i + 1}`} className="w-full rounded-lg border border-gray-200 object-cover" />
                    </a>
                  ) : (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 rounded-lg border border-gray-200 p-3 text-sm text-ink hover:bg-surface-sunken"
                    >
                      <FileDown className="h-4 w-4 shrink-0" />
                      {decodeURIComponent(url.split('/').pop() ?? `File ${i + 1}`)}
                    </a>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Right: Scoring form */}
        <div className="w-full lg:w-96 lg:flex-shrink-0">
          <div className="card sticky top-4 p-5">
            <h2 className="font-semibold text-ink">Scoring</h2>

            {submitted && (
              <div className="mt-3 flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <span className="flex items-center gap-2"><Check className="h-4 w-4" /> Scores saved</span>
                <button className="text-xs font-medium underline" onClick={() => setSubmitted(false)}>Edit</button>
              </div>
            )}

            <div className="mt-4 space-y-5">
              {event.scoringCriteria.map((criterion) => (
                <div key={criterion.name} className="rounded-lg border border-gray-100 p-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">{criterion.name}</label>
                    <span className="text-xs text-ink-tertiary">
                      Weight: {criterion.weight}%
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="number"
                      className="input-base w-20 text-center"
                      value={scores[criterion.name] ?? 0}
                      onChange={(e) =>
                        handleScoreChange(
                          criterion.name,
                          parseInt(e.target.value) || 0,
                          criterion.maxScore,
                        )
                      }
                      min={0}
                      max={criterion.maxScore}
                      disabled={submitted}
                    />
                    <span className="text-sm text-ink-secondary">/ {criterion.maxScore}</span>
                    {/* Visual bar */}
                    <div className="flex-1">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-brand transition-all duration-200"
                          style={{
                            width: `${criterion.maxScore > 0 ? ((scores[criterion.name] ?? 0) / criterion.maxScore) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <textarea
                    className="input-base mt-2 text-xs"
                    rows={2}
                    value={comments[criterion.name] ?? ''}
                    onChange={(e) =>
                      setComments((prev) => ({ ...prev, [criterion.name]: e.target.value }))
                    }
                    placeholder={`Comment on ${criterion.name} (optional)`}
                    maxLength={500}
                    disabled={submitted}
                  />
                </div>
              ))}
            </div>

            {/* Weighted total */}
            <div className="mt-5 flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <span className="text-sm font-semibold text-ink">Weighted Total</span>
              <span className="text-lg font-bold text-brand">
                {weightedTotal.toFixed(1)} / {maxWeightedTotal}
              </span>
            </div>

            {/* Submit */}
            <button
              className="btn-primary mt-4 w-full flex items-center justify-center gap-2"
              onClick={handleSubmitScores}
              disabled={submittingScores || submitted}
            >
              {submittingScores ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : submitted ? (
                <Check className="h-4 w-4" />
              ) : null}
              {submittingScores ? 'Submitting...' : submitted ? 'Scores Submitted' : 'Submit Scores'}
            </button>
          </div>

          {/* AI Judging Summary — organizers/admins only */}
          {canViewAllScores && (
            <div className="mt-6">
              {!judgingSummary && (
                <button
                  className="btn-secondary inline-flex w-full items-center justify-center gap-1.5 text-sm"
                  onClick={handleGenerateJudgingSummary}
                  disabled={judgingSummaryLoading}
                >
                  {judgingSummaryLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {judgingSummaryLoading ? 'Generating Judging Summary...' : 'Generate AI Judging Summary'}
                </button>
              )}
              {judgingSummaryError && (
                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {judgingSummaryError}
                </div>
              )}
              {judgingSummary && (
                <div className="card border-purple-200 bg-purple-50/30 p-4">
                  <button
                    className="flex w-full items-center justify-between"
                    onClick={() => setJudgingSummaryExpanded(!judgingSummaryExpanded)}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                      <Sparkles className="h-4 w-4" /> AI Judging Summary
                    </span>
                    {judgingSummaryExpanded ? (
                      <ChevronUp className="h-4 w-4 text-purple-600" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-purple-600" />
                    )}
                  </button>
                  {judgingSummaryExpanded && (
                    <div className="mt-3 space-y-3">
                      <p className="text-sm text-ink leading-relaxed">{judgingSummary.eventSummary}</p>
                      {judgingSummary.teamSummaries.map((ts) => (
                        <div key={ts.teamId} className={`rounded-lg border p-3 ${ts.teamId === teamId ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-ink">{ts.teamName}</span>
                            <span className="text-xs font-medium text-ink-tertiary">Avg: {ts.averageScore}</span>
                          </div>
                          {ts.strengths && (
                            <div className="mt-1">
                              <span className="text-xs font-semibold text-green-700">Strengths: </span>
                              <span className="text-xs text-green-800">{ts.strengths}</span>
                            </div>
                          )}
                          {ts.weaknesses && (
                            <div className="mt-1">
                              <span className="text-xs font-semibold text-amber-700">Weaknesses: </span>
                              <span className="text-xs text-amber-800">{ts.weaknesses}</span>
                            </div>
                          )}
                          {ts.keyTakeaways && (
                            <div className="mt-1">
                              <span className="text-xs font-semibold text-blue-700">Takeaways: </span>
                              <span className="text-xs text-blue-800">{ts.keyTakeaways}</span>
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        className="btn-ghost text-xs text-purple-700"
                        onClick={handleGenerateJudgingSummary}
                        disabled={judgingSummaryLoading}
                      >
                        {judgingSummaryLoading ? 'Regenerating...' : 'Regenerate'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
